// src/pages/budgeting/fobudgeting/SegmentBudgetingForm.jsx - UPDATED WITH FEEDBACK FIXES
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, X, Save, Package, FileDown, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import ItemPickerModal from '../../../components/boq/ItemPickerModal';
import foSegmentBOQService from '../../../services/foSegmentBOQService';
import foSegmentRAPService from '../../../services/foSegmentRAPService';
import budgetCategoryService from '../../../services/budgetCategoryService';
import itemService from '../../../services/itemService';
import { exportFOSegmentRAPDetailToPDF, exportFOSegmentRAPDetailToExcel } from '../../../utils/foSegmentRAPDetailExport';

const SegmentBudgetingForm = ({ segment, onBack, onSave }) => {
  // ============= STATE MANAGEMENT =============
  
  // Categories data
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // RAP data per category: { categoryId: { items: [...], boqSubtotal: 0, rapSubtotal: 0 } }
  const [rapData, setRAPData] = useState({});
  
  // UI state
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Item Picker Modal
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  
  // Edit Item Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Price coefficient (from segment's main area)
  const [priceCoefficient, setPriceCoefficient] = useState(1.0);

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    console.log('🔍 SEGMENT DATA:', segment);
    
    // Initialize price coefficient
    const coefficientValue = segment.priceCoefficient || 
                             segment.price_coefficient || 
                             1.0;
    console.log('✅ COEFFICIENT:', coefficientValue);
    setPriceCoefficient(coefficientValue);
    
    loadCategories();
    loadBOQAndRAP();
  }, [segment.id]);

  // ============= DATA LOADING =============
  
  /**
   * Load FO Budget Categories (001-005)
   */
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      console.log('📂 Loading budget categories...');
      const allCategories = await budgetCategoryService.getAllCategories();
      
      // Filter only FO categories
      const foCategories = allCategories.filter(cat => cat.projectType === 'FO');
      
      if (foCategories.length === 0) {
        console.warn('⚠️ No FO categories found!');
        toast.error('No FO budget categories available');
        return;
      }
      
      setCategories(foCategories);
      
      // Expand all categories by default
      setExpandedCategories(foCategories.map(cat => cat.id));
      
      console.log('✅ Loaded FO categories:', foCategories);
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      toast.error('Failed to load budget categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };
  
  /**
   * Load BOQ and RAP data
   */
  const loadBOQAndRAP = async () => {
    setIsLoading(true);
    try {
      console.log('📂 Loading BOQ and RAP data...');
      
      // Try to load existing RAPs first
      const raps = await foSegmentRAPService.getRAPsBySegmentId(segment.id);
      
      if (raps && raps.length > 0) {
        // RAP exists - use RAP data
        console.log('✅ Found existing RAPs:', raps);
        const transformed = {};
        
        raps.forEach(rap => {
          transformed[rap.budgetCategoryId] = {
            rapId: rap.id,
            items: rap.items.map(item => ({
              ...item,
              tempId: `rap-${item.id}`,
            })),
            boqSubtotal: rap.boqSubtotalAmount || 0,
            rapSubtotal: rap.rapSubtotalAmount || 0,
            remarks: rap.remarks || ''
          };
        });
        
        setRAPData(transformed);
        console.log('✅ Loaded RAP data:', transformed);
        
      } else {
        // No RAP - try to load BOQ and convert to RAP format
        console.log('📂 No RAP found, loading BOQ data...');
        const boqs = await foSegmentBOQService.getBOQsBySegmentId(segment.id);
        
        if (boqs && boqs.length > 0) {
          console.log('✅ Found BOQs, converting to RAP format:', boqs);
          const transformed = {};
          
          boqs.forEach(boq => {
            // Convert BOQ items to RAP format
            const rapItems = (boq.items || []).map((boqItem, index) => ({
              tempId: `boq-${boqItem.id}`,
              sourceType: 'BOQ',
              boqItemId: boqItem.id,
              itemId: boqItem.itemId,
              itemNumber: boqItem.itemNumber,
              itemCode: boqItem.itemCode,
              itemDescription: boqItem.itemDescription,
              itemType: boqItem.itemType,
              unit: boqItem.unit,
              volume: boqItem.volume,
              // BOQ/RAB columns
              boqUnitPrice: boqItem.finalUnitPrice,
              boqTotalPrice: boqItem.totalPrice,
              // RAP control
              isNotUsed: false,
              isOverride: false,
              overriddenRapUnitPrice: null,
              // RAP columns (initially same as BOQ)
              rapUnitPrice: boqItem.finalUnitPrice,
              rapTotalPrice: boqItem.totalPrice,
              priceCoefficient: boqItem.priceCoefficient,
              sequenceOrder: index,
              remarks: boqItem.remarks || '',
              // UI flags
              canDelete: false,
              isFromBOQ: true
            }));
            
            transformed[boq.budgetCategoryId] = {
              items: rapItems,
              boqSubtotal: boq.subtotalAmount || 0,
              rapSubtotal: boq.subtotalAmount || 0, // Initially same as BOQ
              remarks: ''
            };
          });
          
          setRAPData(transformed);
          console.log('✅ Converted BOQ to RAP format:', transformed);
        } else {
          console.log('ℹ️ No BOQ or RAP found, starting fresh');
        }
      }
      
    } catch (error) {
      console.error('❌ Error loading BOQ/RAP:', error);
      toast.error('Failed to load budget data');
    } finally {
      setIsLoading(false);
    }
  };

  // ============= HELPER FUNCTIONS =============
  
  /**
   * Format currency
   */
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  /**
   * Format volume (number only, no currency)
   */
  const formatVolume = (value) => {
    if (!value && value !== 0) return '0';
    return value.toLocaleString('id-ID', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 4 
    });
  };
  
  /**
   * Calculate BOQ subtotal for a category
   */
  const calculateBOQSubtotal = (categoryId) => {
    const items = rapData[categoryId]?.items || [];
    return items
      .filter(item => item.sourceType === 'BOQ')
      .reduce((sum, item) => sum + (parseFloat(item.boqTotalPrice) || 0), 0);
  };
  
  /**
   * Calculate RAP subtotal for a category (excludes Not Used items)
   */
  const calculateRAPSubtotal = (categoryId) => {
    const items = rapData[categoryId]?.items || [];
    return items
      .filter(item => !item.isNotUsed)
      .reduce((sum, item) => sum + (parseFloat(item.rapTotalPrice) || 0), 0);
  };
  
  /**
   * Calculate grand totals
   */
  const calculateGrandTotals = () => {
    const boqTotal = categories.reduce((sum, cat) => 
      sum + calculateBOQSubtotal(cat.id), 0
    );
    
    const rapTotal = categories.reduce((sum, cat) => 
      sum + calculateRAPSubtotal(cat.id), 0
    );
    
    return { boqTotal, rapTotal };
  };

  // ============= EVENT HANDLERS =============
  
  /**
   * Toggle category expand/collapse
   */
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };
  
  /**
   * Handle Add Item
   */
  const handleAddItem = (categoryId) => {
    setCurrentCategoryId(categoryId);
    setShowItemPicker(true);
  };
  
  /**
   * Handle item selection from picker
   */
  const handleItemSelect = async (item) => {
    if (!currentCategoryId) return;
    
    try {
      // Calculate RAP unit price (item default price × coefficient)
      const basePrice = item.defaultPrice || 0;
      const rapUnitPrice = basePrice * priceCoefficient;
      
      // Create new RAP item
      const newItem = {
        tempId: `new-${Date.now()}`,
        sourceType: 'NEW',
        boqItemId: null,
        itemId: item.id,
        itemNumber: item.itemNumber,
        itemCode: item.itemCode,
        itemDescription: item.itemName,
        itemType: item.itemType,
        unit: item.unit,
        volume: 1, // Default volume
        // BOQ/RAB columns (empty for new items)
        boqUnitPrice: null,
        boqTotalPrice: null,
        // RAP control
        isNotUsed: false,
        isOverride: false,
        overriddenRapUnitPrice: null,
        // RAP columns
        rapUnitPrice: rapUnitPrice,
        rapTotalPrice: rapUnitPrice * 1, // volume = 1
        priceCoefficient: priceCoefficient,
        sequenceOrder: (rapData[currentCategoryId]?.items?.length || 0) + 1,
        remarks: '',
        // UI flags
        canDelete: true,
        isFromBOQ: false
      };
      
      // Add to RAP data
      setRAPData(prev => ({
        ...prev,
        [currentCategoryId]: {
          ...prev[currentCategoryId],
          items: [...(prev[currentCategoryId]?.items || []), newItem]
        }
      }));
      
      setShowItemPicker(false);
      toast.success('Item added successfully');
      
    } catch (error) {
      console.error('❌ Error adding item:', error);
      toast.error('Failed to add item');
    }
  };
  
  /**
   * Handle Edit Item
   */
  const handleEditItem = (categoryId, item) => {
    setEditingItem({ categoryId, item });
    setEditFormData({
      volume: item.volume.toString(),
      isNotUsed: item.isNotUsed,
      isOverride: item.isOverride,
      overriddenRapUnitPrice: item.overriddenRapUnitPrice?.toString() || '',
      remarks: item.remarks || ''
    });
    setShowEditModal(true);
  };
  
  /**
   * Handle Delete Item (only for NEW items)
   */
  const handleDeleteItem = (categoryId, itemTempId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    setRAPData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        items: prev[categoryId].items.filter(item => item.tempId !== itemTempId)
      }
    }));
    
    toast.success('Item deleted successfully');
  };
  
  /**
   * Handle inline override price change (FIX #2)
   */
  const handleInlineOverridePrice = (categoryId, itemTempId, newPrice) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if (item.tempId === itemTempId) {
          const updatedItem = { ...item };
          updatedItem.overriddenRapUnitPrice = newPrice;
          updatedItem.rapUnitPrice = newPrice;
          updatedItem.rapTotalPrice = newPrice * updatedItem.volume;
          return updatedItem;
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  /**
   * Handle Checkbox Change (Not Used, Override)
   */
  const handleCheckboxChange = (categoryId, itemTempId, field, value) => {
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if (item.tempId === itemTempId) {
          const updatedItem = { ...item, [field]: value };
          
          // Recalculate RAP prices
          if (field === 'isOverride' && !value) {
            // Reset override
            updatedItem.overriddenRapUnitPrice = null;
            updatedItem.rapUnitPrice = updatedItem.boqUnitPrice || updatedItem.rapUnitPrice;
            updatedItem.rapTotalPrice = updatedItem.rapUnitPrice * updatedItem.volume;
          }
          
          if (field === 'isNotUsed') {
            // Set RAP total to 0 if Not Used
            updatedItem.rapTotalPrice = value ? 0 : (updatedItem.rapUnitPrice * updatedItem.volume);
          }
          
          return updatedItem;
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  /**
   * Handle Save Edit Modal
   */
  const handleSaveEdit = () => {
    const { categoryId, item } = editingItem;
    
    // Validation
    const volume = parseFloat(editFormData.volume) || 0;
    if (volume <= 0) {
      toast.error('Volume must be greater than 0');
      return;
    }
    
    if (editFormData.isOverride) {
      const overridePrice = parseFloat(editFormData.overriddenRapUnitPrice) || 0;
      if (overridePrice <= 0) {
        toast.error('Override price must be greater than 0');
        return;
      }
    }
    
    setRAPData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(itm => {
        if (itm.tempId === item.tempId) {
          const isOverride = editFormData.isOverride;
          const overriddenPrice = editFormData.overriddenRapUnitPrice 
            ? parseFloat(editFormData.overriddenRapUnitPrice) 
            : null;
          
          // Calculate RAP unit price
          let rapUnitPrice = itm.rapUnitPrice;
          if (isOverride && overriddenPrice !== null) {
            rapUnitPrice = overriddenPrice;
          } else if (itm.sourceType === 'BOQ') {
            rapUnitPrice = itm.boqUnitPrice;
          }
          
          // Calculate RAP total price
          const rapTotalPrice = editFormData.isNotUsed ? 0 : (rapUnitPrice * volume);
          
          return {
            ...itm,
            volume,
            isNotUsed: editFormData.isNotUsed,
            isOverride: editFormData.isOverride,
            overriddenRapUnitPrice: overriddenPrice,
            rapUnitPrice,
            rapTotalPrice,
            remarks: editFormData.remarks,
            // Update BOQ total if volume changed
            boqTotalPrice: itm.sourceType === 'BOQ' 
              ? (itm.boqUnitPrice * volume) 
              : null
          };
        }
        return itm;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
    
    setShowEditModal(false);
    setEditingItem(null);
    toast.success('Item updated successfully');
  };
  
  /**
   * Handle Save RAP
   */
  const handleSave = async () => {
    if (!window.confirm('Save RAP for this segment?')) return;
    
    setIsSaving(true);
    
    try {
      // Save each category that has items
      const savePromises = categories
        .filter(category => rapData[category.id]?.items?.length > 0)
        .map(category => {
          const categoryData = rapData[category.id];
          
          // Prepare request
          const request = {
            segmentId: segment.id,
            budgetCategoryId: category.id,
            remarks: categoryData.remarks || '',
            items: categoryData.items.map(item => ({
              sourceType: item.sourceType,
              itemId: item.itemId,
              boqItemId: item.boqItemId,
              volume: parseFloat(item.volume) || 0,
              isNotUsed: item.isNotUsed || false,
              isOverride: item.isOverride || false,
              overriddenRapUnitPrice: item.isOverride && item.overriddenRapUnitPrice 
                ? parseFloat(item.overriddenRapUnitPrice) 
                : null,
              sequenceOrder: item.sequenceOrder || 0,
              remarks: item.remarks || ''
            }))
          };
          
          return foSegmentRAPService.saveRAP(request);
        });
      
      await Promise.all(savePromises);
      
      toast.success('RAP saved successfully!');
      
      // Callback to parent
      if (onSave) {
        onSave(segment.id);
      }
      
    } catch (error) {
      console.error('❌ Error saving RAP:', error);
      toast.error('Failed to save RAP');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle Export RAP Detail to PDF
   */
  const handleExportDetailPDF = () => {
    exportFOSegmentRAPDetailToPDF(segment, rapData, categories, priceCoefficient);
  };

  /**
   * Handle Export RAP Detail to Excel
   */
  const handleExportDetailExcel = () => {
    exportFOSegmentRAPDetailToExcel(segment, rapData, categories, priceCoefficient);
  };  

  // ============= RENDER HELPERS =============
  
  /**
   * Render single item row
   */
  const renderItemRow = (categoryId, item, index) => {
    const isNotUsedStyle = item.isNotUsed ? 'opacity-50 bg-gray-100' : '';
    
    return (
      <tr key={item.tempId} className={`border-b border-gray-200 hover:bg-gray-50 ${isNotUsedStyle}`}>
        {/* No */}
        <td className="px-4 py-3 text-center text-sm font-medium text-gray-900 border-r border-gray-200">
          {index + 1}
        </td>
        
        {/* Description */}
        <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {item.itemCode || item.itemNumber}
                </span>
                {item.sourceType === 'NEW' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold">
                    NEW
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-gray-900 mt-0.5">
                {item.itemDescription}
              </div>
            </div>
          </div>
        </td>
        
        {/* Unit */}
        <td className="px-4 py-3 text-center text-sm text-gray-900 border-r border-gray-200">
          {item.unit}
        </td>
        
        {/* Volume - FIX #3: No "Rp" prefix */}
        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 border-r border-gray-200">
          {formatVolume(item.volume)}
        </td>
        
        {/* BOQ/RAB Unit Price */}
        <td className="px-4 py-3 text-right text-sm text-gray-900 border-r border-gray-200">
          {item.boqUnitPrice ? formatCurrency(item.boqUnitPrice) : '-'}
        </td>
        
        {/* BOQ/RAB Price */}
        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 border-r border-gray-200">
          {item.boqTotalPrice ? formatCurrency(item.boqTotalPrice) : '-'}
        </td>
        
        {/* Not Used Checkbox */}
        <td className="px-4 py-3 text-center border-r border-gray-200">
          <input
            type="checkbox"
            checked={item.isNotUsed}
            onChange={(e) => handleCheckboxChange(categoryId, item.tempId, 'isNotUsed', e.target.checked)}
            className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
            disabled={item.sourceType === 'NEW' || item.isOverride}
            title={
              item.sourceType === 'NEW' 
                ? 'Not available for NEW items' 
                : item.isOverride
                  ? 'Cannot mark as Not Used when Override is active'
                  : 'Exclude from RAP'
            }
          />
        </td>
        
        {/* Override Checkbox */}
        <td className="px-4 py-3 text-center border-r border-gray-200">
          <input
            type="checkbox"
            checked={item.isOverride}
            onChange={(e) => handleCheckboxChange(categoryId, item.tempId, 'isOverride', e.target.checked)}
            className="w-4 h-4 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
            disabled={item.sourceType === 'NEW' || item.isNotUsed}
            title={
              item.sourceType === 'NEW' 
                ? 'Not available for NEW items'
                : item.isNotUsed
                  ? 'Cannot Override when marked as Not Used'
                  : 'Override RAP price'
            }
          />
        </td>
        
        {/* RAP Unit Price - FIX #1 & #2: Hide when Not Used, Editable when Override */}
        <td className="px-4 py-3 text-right text-sm font-medium text-blue-900 border-r border-gray-200">
          {item.isNotUsed ? (
            <span className="text-gray-400 italic">-</span>
          ) : item.isOverride ? (
            <div className="flex items-center justify-end gap-2">
              <input
                type="number"
                value={item.rapUnitPrice}
                onChange={(e) => handleInlineOverridePrice(categoryId, item.tempId, parseFloat(e.target.value) || 0)}
                className="w-32 px-2 py-1 text-right border border-orange-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Override"
                min="0"
                step="0.01"
              />
              <span className="text-xs text-orange-600 font-medium whitespace-nowrap">OVERRIDE</span>
            </div>
          ) : (
            formatCurrency(item.rapUnitPrice)
          )}
        </td>
        
        {/* RAP Price - FIX #1: Hide when Not Used */}
        <td className="px-4 py-3 text-right text-sm font-bold text-blue-600 border-r border-gray-200">
          {item.isNotUsed ? (
            <span className="text-gray-400 italic">-</span>
          ) : (
            formatCurrency(item.rapTotalPrice)
          )}
        </td>
        
        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handleEditItem(categoryId, item)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            {item.canDelete && (
              <button
                onClick={() => handleDeleteItem(categoryId, item.tempId)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ============= MAIN RENDER =============
  
  const { boqTotal, rapTotal } = calculateGrandTotals();
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isSaving}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                FO Segment Budgeting - Rencana Anggaran Pelaksanaan (RAP)
              </h1>
              <p className="text-gray-600 mt-1">{segment.spanId}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Export Detail Buttons */}
            <div className="flex items-center gap-2 border-r border-gray-300 pr-3">
              <button
                onClick={handleExportDetailPDF}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md"
                title="Export RAP Detail to PDF"
              >
                <FileDown className="w-4 h-4" />
                PDF
              </button>
              
              <button
                onClick={handleExportDetailExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
                title="Export RAP Detail to Excel"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>
            </div>          

            {/* Save RAP Button */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Saving...' : 'Save RAP'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Segment Info */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <h3 className="text-lg font-bold text-gray-800">Segment Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-32 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-600">Span ID</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm text-gray-900">: {segment.spanId}</span>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-32 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-600">Main Area</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm text-gray-900">: {segment.mainArea || segment.main_area}</span>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-32 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-600">Coefficient</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-bold text-blue-600">: ×{priceCoefficient.toFixed(4)}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-32 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-600">Segment Name</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm text-gray-900">: {segment.siteNameAB}</span>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-32 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-600">Distance</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm text-gray-900">
                    : {segment.commercialDistance 
                      ? (segment.commercialDistance / 1000).toFixed(2) 
                      : '0'} km
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Budgeting Table */}
      {isLoading || isLoadingCategories ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading budget data...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(category => {
            const isExpanded = expandedCategories.includes(category.id);
            const items = rapData[category.id]?.items || [];
            const boqSubtotal = calculateBOQSubtotal(category.id);
            const rapSubtotal = calculateRAPSubtotal(category.id);
            
            return (
              <div key={category.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                {/* Category Header */}
                <div
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4 cursor-pointer hover:from-blue-700 hover:to-blue-600 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button className="text-white">
                        <svg
                          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div>
                        <h3 className="text-lg font-bold">
                          {category.categoryCode} - {category.categoryName}
                        </h3>
                        <p className="text-sm text-blue-100 mt-1">
                          {items.length} items • BOQ: {formatCurrency(boqSubtotal)} • RAP: {formatCurrency(rapSubtotal)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddItem(category.id);
                      }}
                      className="flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                </div>

                {/* Category Content */}
                {isExpanded && (
                  <div className="p-4">
                    {items.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No items in this category</p>
                        <p className="text-sm mt-1">Click "Add Item" to start</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full border border-gray-300 rounded-lg">
                          <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                              <th className="px-4 py-3 text-left text-sm font-bold border-r border-blue-500" style={{ width: '60px' }}>NO</th>
                              <th className="px-4 py-3 text-left text-sm font-bold border-r border-blue-500">DESCRIPTION</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" style={{ width: '80px' }}>UNIT</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" style={{ width: '100px' }}>VOL</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" colSpan="2">BOQ/RAB</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" style={{ width: '80px' }}>NOT USED</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" style={{ width: '80px' }}>OVERRIDE</th>
                              <th className="px-4 py-3 text-center text-sm font-bold border-r border-blue-500" colSpan="2">RAP</th>
                              <th className="px-4 py-3 text-center text-sm font-bold" style={{ width: '100px' }}>ACTIONS</th>
                            </tr>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                              <th colSpan="4" className="border-r border-blue-500"></th>
                              <th className="px-4 py-2 text-center text-xs font-bold border-r border-blue-500" style={{ width: '120px' }}>Unit Price</th>
                              <th className="px-4 py-2 text-center text-xs font-bold border-r border-blue-500" style={{ width: '140px' }}>Price</th>
                              <th colSpan="2" className="border-r border-blue-500"></th>
                              <th className="px-4 py-2 text-center text-xs font-bold border-r border-blue-500" style={{ width: '120px' }}>Unit Price</th>
                              <th className="px-4 py-2 text-center text-xs font-bold border-r border-blue-500" style={{ width: '140px' }}>Price</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => renderItemRow(category.id, item, idx))}
                          </tbody>
                          <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
                            <tr>
                              <td colSpan="5" className="px-4 py-3 text-right text-sm text-gray-700 border-r border-gray-300">
                                BOQ Subtotal {category.categoryCode}:
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-gray-900 border-r border-gray-300">
                                {formatCurrency(boqSubtotal)}
                              </td>
                              <td colSpan="3" className="px-4 py-3 text-right text-sm text-gray-700 border-r border-gray-300">
                                RAP Subtotal {category.categoryCode}:
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-blue-600 border-r border-gray-300">
                                {formatCurrency(rapSubtotal)}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Grand Total */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-2">BOQ/RAB Grand Total</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(boqTotal)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">RAP Grand Total</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(rapTotal)}</p>
          </div>
        </div>
      </div>

      {/* Item Picker Modal */}
      <ItemPickerModal
        isOpen={showItemPicker}
        onClose={() => setShowItemPicker(false)}
        onSelect={handleItemSelect}
        filterType="ALL"
      />

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50 sticky top-0 z-10">
              <h3 className="text-lg font-bold text-gray-900">Edit Item</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Item Info */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-xs text-gray-600 mb-1">
                  {editingItem.item.itemCode || editingItem.item.itemNumber}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {editingItem.item.itemDescription}
                </div>
              </div>

              {/* Volume */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Volume <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={editFormData.volume}
                  onChange={(e) => setEditFormData({ ...editFormData, volume: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Unit: {editingItem.item.unit}
                </p>
              </div>

              {/* Not Used Checkbox (only for BOQ items) */}
              {editingItem.item.sourceType === 'BOQ' && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editFormData.isNotUsed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEditFormData({ 
                        ...editFormData, 
                        isNotUsed: checked,
                        // Uncheck override if marking as not used
                        isOverride: checked ? false : editFormData.isOverride
                      });
                    }}
                    className="w-4 h-4 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                    disabled={editFormData.isOverride}
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Mark as Not Used (exclude from RAP)
                  </label>
                </div>
              )}

              {/* Override Checkbox (only for BOQ items) */}
              {editingItem.item.sourceType === 'BOQ' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editFormData.isOverride}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditFormData({ 
                          ...editFormData, 
                          isOverride: checked,
                          // Reset override price if unchecked
                          overriddenRapUnitPrice: checked ? editFormData.overriddenRapUnitPrice : '',
                          // Uncheck not used if marking as override
                          isNotUsed: checked ? false : editFormData.isNotUsed
                        });
                      }}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
                      disabled={editFormData.isNotUsed}
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Override RAP Unit Price
                    </label>
                  </div>
                  
                  {editFormData.isOverride && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Override RAP Unit Price <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={editFormData.overriddenRapUnitPrice}
                        onChange={(e) => setEditFormData({ ...editFormData, overriddenRapUnitPrice: e.target.value })}
                        placeholder="Enter override price"
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        min="0"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Current BOQ price: {formatCurrency(editingItem.item.boqUnitPrice || 0)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={editFormData.remarks}
                  onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Optional notes..."
                />
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-blue-600 via-red-600 to-blue-600"></div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentBudgetingForm;